/**
 * ENG-5459 — scrape Claude Code's "N MCP servers failed · /mcp" status
 * banner from a pane.log tail.
 *
 * Why this exists: mcp-presence-reaper (ENG-5279/5292/5285) checks for
 * live MCP child *processes* of the claude PID. An MCP whose
 * `npx @composio/mcp start --url ...` process is alive but whose URL
 * 401s on every tool call passes the reaper's heuristic — process is
 * up, reaper thinks all is well, but the integration is functionally
 * dead. Observed 2026-05-24 with Pepper post-Composio-revocation: 4
 * MCP processes alive, 0 functional, reaper silent.
 *
 * Claude Code already detects this and renders the banner. The
 * manager scrapes the count from pane.log each poll, persists via
 * `/host/mcp-banner-observation`, and the webapp surfaces it alongside
 * the existing `needsReconnect` signal.
 *
 * Pure function — caller passes a tail of pane.log content and gets
 * back the most-recent observed count (or null if no banner present).
 * Tests drive synthetic strings rather than reading real pane.log.
 */

/**
 * Matches the banner Claude Code renders at the bottom of its TUI:
 *
 *   "1 MCP server failed · /mcp"
 *   "4 MCP servers failed · /mcp"
 *
 * The "·" is U+00B7 MIDDLE DOT — copy carefully if regenerating.
 * Singular "server" vs plural "servers" both match (Claude renders the
 * singular form when count === 1).
 */
const BANNER_RE = /(\d+)\s+MCP\s+server(?:s)?\s+failed\s*[·•]\s*\/mcp/gi;

/**
 * Extract the LAST matched banner from a pane tail. Last-wins because
 * the banner re-renders on every screen frame — earlier matches are
 * stale; the most recent reflects the current state.
 *
 * Returns `null` when no banner is present. **Null is "not detected
 * this tick", not "definitely zero"** — the caller should treat null
 * as "unknown / observation skipped". For the agents.mcp_failed_banner_count
 * column the API writes 0 only on an explicit observation of 0, not on
 * a missing banner.
 *
 * Returns 0 when the banner is genuinely "0 MCP servers failed" —
 * Claude Code doesn't usually render this (it omits the banner when
 * healthy), but we treat 0 as a valid observation if seen, so a future
 * Claude Code change that always renders the banner is handled.
 */
export function scrapeMcpFailedBannerCount(paneTail: string): number | null {
  if (!paneTail) return null;
  let last: number | null = null;
  let m: RegExpExecArray | null;
  // Reset lastIndex so consecutive calls with the same regex don't skip.
  BANNER_RE.lastIndex = 0;
  while ((m = BANNER_RE.exec(paneTail)) !== null) {
    const n = parseInt(m[1]!, 10);
    if (Number.isFinite(n) && n >= 0) last = n;
  }
  return last;
}

/**
 * Convenience: returns true when the scraped tail shows ANY failing
 * MCPs. Useful for the manager's "should I bother POSTing this tick?"
 * gate when the previous count was already 0.
 */
export function paneHasFailingMcps(paneTail: string): boolean {
  const n = scrapeMcpFailedBannerCount(paneTail);
  return typeof n === 'number' && n > 0;
}
